import { useEffect, useMemo, useState } from "react";
import axios from "../../configApi/axiosConfig";
import { APIURL } from "../../configApi/apiConfig";

/**
 * Traduce una fila cruda de la API (alias `i_*` de TypeORM) a la forma
 * interna limpia que consume el resto del sidebar. Es el ÚNICO lugar que
 * conoce el contrato del backend: si algún día cambia el prefijo de alias,
 * o se agrega un campo nuevo (ej. `i_badge_count` para notificaciones),
 * solo se toca esta función.
 */
function normalizeMenuItem(raw) {
  return {
    id: raw.i_idmenu_items,
    name: raw.i_name,
    route: raw.i_route && raw.i_route !== "#" ? raw.i_route : null,
    icon: raw.i_icon,
    parentId:
      raw.i_parent_id === null || raw.i_parent_id === undefined || Number(raw.i_parent_id) === 0
        ? null
        : Number(raw.i_parent_id),
    // Campo opcional y todavía no expuesto por el backend: si en el futuro
    // `menu-item-role/userId/menu` agrega un conteo (pendientes, alertas,
    // etc.), el badge aparece solo con este mapeo, sin tocar UI.
    badgeCount: raw.i_badge_count ?? null,
  };
}

function buildTree(flatItems) {
  const byParent = new Map();
  for (const item of flatItems) {
    const bucket = byParent.get(item.parentId) || [];
    bucket.push(item);
    byParent.set(item.parentId, bucket);
  }

  const attachChildren = (parentId) =>
    (byParent.get(parentId) || []).map((item) => ({
      ...item,
      children: attachChildren(item.id),
    }));

  return attachChildren(null);
}

/**
 * Obtiene el menú del usuario autenticado y lo deja listo como árbol.
 * El filtrado por permisos/rol NO ocurre aquí: la API ya devuelve
 * únicamente los ítems que el usuario tiene autorizados (join contra
 * menu_item_roles en el backend) — este hook solo normaliza y estructura.
 */
export function useMenuData(idUsuario) {
  const [flatItems, setFlatItems] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error

  useEffect(() => {
    if (!idUsuario) return;

    let cancelled = false;

    const fetchMenu = async () => {
      setStatus("loading");
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setStatus("error");
          return;
        }

        const { data } = await axios.get(APIURL.getMenu(), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;
        setFlatItems((data || []).map(normalizeMenuItem));
        setStatus("ready");
      } catch (error) {
        console.error("Error obteniendo el menú:", error);
        if (!cancelled) setStatus("error");
      }
    };

    fetchMenu();
    return () => {
      cancelled = true;
    };
  }, [idUsuario]);

  const tree = useMemo(() => buildTree(flatItems), [flatItems]);

  return { tree, flatItems, status };
}
